/**
 * Reports Hooks - React hooks for reports CRUD operations
 *
 * Provides React Query-based hooks for managing scan reports
 * with caching, loading states, and error handling.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsApi, type CreateReportData, type UpdateReportData } from '@/lib/reports-api';
import { useOrgContext } from '@/contexts/OrgContext';

// Shared retry config for all report queries — matches global QueryClient retry: 1
const RETRY_CONFIG = {
  retry: 1,
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000),
} as const;

// While a scan in the list is still queued/running, re-poll this often so its
// row transitions to the terminal status on its own.
const REPORTS_PENDING_POLL_MS = 5000;

// When the cache is fresh but contains no pending scans, still re-poll at this
// interval so a newly-submitted scan (which invalidates the cache but doesn't
// force an immediate refetch on unmounted queries) appears within a few seconds
// of the user navigating to the Reports page.
const REPORTS_IDLE_POLL_MS = 10_000;

// Query keys for React Query — scoped by activeOrgId for cache isolation
export const REPORTS_QUERY_KEYS = {
  all: ['reports'] as const,
  lists: (orgId: string | null) => [...REPORTS_QUERY_KEYS.all, 'list', orgId] as const,
  list: (limit: number, offset: number, orgId: string | null) =>
    [...REPORTS_QUERY_KEYS.lists(orgId), limit, offset] as const,
  details: () => [...REPORTS_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...REPORTS_QUERY_KEYS.details(), id] as const,
  stats: (orgId: string | null) => [...REPORTS_QUERY_KEYS.all, 'stats', orgId] as const,
};

/**
 * Hook to get all reports for the current user, scoped to active org context
 */
export function useReports(limit: number = 50, offset: number = 0) {
  const { activeOrgId, orgHeader } = useOrgContext();
  return useQuery({
    queryKey: REPORTS_QUERY_KEYS.list(limit, offset, activeOrgId),
    queryFn: () => reportsApi.getReports(limit, offset, orgHeader),
    // Keep staleTime short so navigating to the page always shows fresh data.
    // Previously 5 min — too long; a newly-submitted scan invalidated the cache
    // but the query didn't refetch until a component re-subscribed, causing a
    // 30-60 s delay before the new row appeared on the Reports page.
    staleTime: 10_000,
    // The /v2/reports list reads the Prisma scan_reports table directly (not the
    // KV-backed fetchScan that getScan/the completion toast use), so a row's
    // status can lag a beat behind completion. Poll at two rates:
    //   - REPORTS_PENDING_POLL_MS (5 s) while any scan is queued/running
    //   - REPORTS_IDLE_POLL_MS (10 s) otherwise, so new scan rows appear quickly
    //     after submission without hammering the API during quiet periods.
    refetchInterval: (query) => {
      const reports = query.state.data?.reports ?? [];
      const hasPending = reports.some((r) => r.status === 'queued' || r.status === 'running');
      return hasPending ? REPORTS_PENDING_POLL_MS : REPORTS_IDLE_POLL_MS;
    },
    ...RETRY_CONFIG,
  });
}

/**
 * Hook to get a specific report by ID
 */
export function useReport(reportId: string) {
  return useQuery({
    queryKey: REPORTS_QUERY_KEYS.detail(reportId),
    queryFn: () => reportsApi.getReport(reportId),
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000,
    ...RETRY_CONFIG,
  });
}

/**
 * Hook to get user scan statistics scoped to active org context
 */
export function useReportStats() {
  const { activeOrgId, orgHeader } = useOrgContext();
  return useQuery({
    queryKey: REPORTS_QUERY_KEYS.stats(activeOrgId),
    queryFn: () => reportsApi.getStats(orgHeader),
    staleTime: 5 * 60 * 1000, // Aligned with global default
    ...RETRY_CONFIG,
  });
}

/**
 * Hook to create a new report
 */
export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReportData) => reportsApi.createReport(data),
    onSuccess: () => {
      // Invalidate all report lists and stats across orgs
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to update an existing report
 */
export function useUpdateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: UpdateReportData }) =>
      reportsApi.updateReport(reportId, data),
    onSuccess: (updatedReport) => {
      // Update the specific report in cache
      queryClient.setQueryData(REPORTS_QUERY_KEYS.detail(updatedReport.scanId), updatedReport);
      // Invalidate lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to delete a report
 */
export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => reportsApi.deleteReport(reportId),
    onSuccess: (_, reportId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: REPORTS_QUERY_KEYS.detail(reportId) });
      // Invalidate lists and stats across orgs
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to cleanup old reports
 */
export function useCleanupReports() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keepCount: number = 10) => reportsApi.cleanupReports(keepCount),
    onSuccess: () => {
      // Invalidate all reports data
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
    },
  });
}
